// Mount point for the demo tour. Deliberately tiny: the modal itself, and
// video.js behind it (~200 KB of JS plus 12 KB of CSS), is loaded on the first
// open, so a visitor who never presses play never downloads it. Before this
// split it sat in every page's critical path, and PageSpeed reported 85% of it
// as unused on load.
//
// The window.openDemoTour / closeDemoTour contract is unchanged; every call
// site in the site keeps working as before.

import React from 'react';
import { VIDEOS, emitDemoEvent } from './demoVideos.js';

const DemoVideoModal = React.lazy(() => import('./DemoVideoModal.jsx'));

// Start fetching the chunk before it is needed (pointer over a play control,
// say). Idempotent; the same promise is reused by the lazy boundary.
let warmed = null;
export function preloadDemoVideoModal() {
  if (!warmed) warmed = import('./DemoVideoModal.jsx');
  return warmed;
}

export function DemoVideoModalRoot() {
  const [state, setState] = React.useState({ open: false, source: 'unknown', video: 'demo' });

  React.useEffect(() => {
    // `video` picks a VIDEOS entry; unknown keys fall back to the demo tour.
    window.openDemoTour = (source = 'unknown', video = 'demo') => {
      const key = VIDEOS[video] ? video : 'demo';
      emitDemoEvent('demo_tour_opened', source, key);
      preloadDemoVideoModal();
      setState({ open: true, source, video: key });
    };
    window.closeDemoTour = () => setState({ open: false, source: 'unknown', video: 'demo' });
    // Deep link: /product?demo=1 (the board-pack email's walkthrough link)
    // or a #demo hash opens the tour on arrival. Browsers may still block
    // sound-on autoplay without a gesture; the big play control remains.
    try {
      const wantsDemo =
        new URLSearchParams(window.location.search).get('demo') === '1' ||
        window.location.hash === '#demo';
      if (wantsDemo) window.openDemoTour('deep-link');
    } catch (_) { /* malformed URL — never block mount */ }
    return () => {
      window.openDemoTour = null;
      window.closeDemoTour = null;
    };
  }, []);

  if (!state.open) return null;
  return (
    <React.Suspense fallback={null}>
      <DemoVideoModal
        source={state.source}
        video={state.video}
        onClose={() => setState({ open: false, source: 'unknown', video: 'demo' })}
      />
    </React.Suspense>
  );
}
